import { useState, useEffect } from "react";
import { Dismiss24Regular, Document24Regular, Search24Regular, Delete24Regular, Eye24Regular, Open24Regular } from "@fluentui/react-icons";
import { IconButton, Checkbox } from "@fluentui/react";
import { SearchModal } from "../SearchModal/SearchModal";
import { ConfirmDeleteModal } from "../ConfirmDeleteModal/ConfirmDeleteModal";
import { DescriptionModal } from "../DescriptionModal";
import { formatStudyTitle } from "../../utils/formatters";
import { ActionButton } from "../ActionButton";
import styles from "./SelectedSourcesModal.module.css";

interface Source {
    id: number;
    title: string;
    type: string;
    pages: number;
    lastAccessed: string;
    selected?: boolean;
    year?: string;
    subdireccion?: string;
    categoria?: string;
    filepaths: string[];
    sharepoint?: string;
    description?: string;
    id_dnp?: string;
}

interface Props {
    isOpen: boolean;
    onToggle: () => void;
    onSelectedSourcesChange?: (selectedSources: string[]) => void;
    onSelectedSourcesCountChange?: (count: number) => void;
    isMobile?: boolean;
    sources?: Source[];
    onSourcesChange?: (sources: Source[]) => void;
    defaultSearchOpen?: boolean;
    mode?: 'manage' | 'search';
}

const dummySources: Source[] = [
];

export const SelectedSourcesModal = ({ isOpen, onToggle, onSelectedSourcesChange, onSelectedSourcesCountChange, isMobile, sources: externalSources, onSourcesChange, defaultSearchOpen = false, mode = 'manage' }: Props) => {
    const [internalSources, setInternalSources] = useState<Source[]>(dummySources);
    const [isSearchModalOpen, setIsSearchModalOpen] = useState(defaultSearchOpen || mode === 'search');
    const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
    const [sourceToDelete, setSourceToDelete] = useState<Source | null>(null);
    const [isDeletingAll, setIsDeletingAll] = useState(false);
    const [descriptionModal, setDescriptionModal] = useState<{
        isOpen: boolean;
        title: string;
        description: string;
        id_dnp: string;
    }>({ isOpen: false, title: "", description: "", id_dnp: "" });

    // Use external sources if provided, otherwise use internal state
    const sources = externalSources !== undefined ? externalSources : internalSources;
    const setSources = (value: Source[] | ((prev: Source[]) => Source[])) => {
        if (onSourcesChange) {
            if (typeof value === 'function') {
                onSourcesChange(value(sources));
            } else {
                onSourcesChange(value);
            }
        } else {
            if (typeof value === 'function') {
                setInternalSources(value);
            } else {
                setInternalSources(value);
            }
        }
    };

    // Notify parent component when selected sources change
    useEffect(() => {
        const selectedSourcesList = sources.filter(source => source.selected);

        if (onSelectedSourcesChange) {
            const selectedSourceTitles = selectedSourcesList.flatMap(source => source.filepaths);
            onSelectedSourcesChange(selectedSourceTitles);
        }

        if (onSelectedSourcesCountChange) {
            onSelectedSourcesCountChange(selectedSourcesList.length);
        }
    }, [sources, onSelectedSourcesChange, onSelectedSourcesCountChange]);

    const handleSourceSelect = (id: number, selected: boolean) => {
        setSources(prev =>
            prev.map(source =>
                source.id === id ? { ...source, selected } : source
            )
        );
    };

    const handleAddSources = (newSources: any[]) => {
        setSources(prev => {
            // newSources represents exactly what should stay in the cart
            // We want to preserve existing metadata (like lastAccessed) for items that stay
            const existingMap = new Map(prev.map(s => [(s.id_dnp || s.id).toString(), s]));

            return newSources.map(newS => {
                const key = (newS.id_dnp || newS.id).toString();
                const existing = existingMap.get(key);

                if (existing) {
                    return { ...existing, selected: true };
                }

                return {
                    id: newS.id,
                    title: newS.title,
                    type: newS.type,
                    pages: newS.filepaths?.length || 0,
                    lastAccessed: "Recién agregado",
                    selected: true,
                    year: newS.year,
                    subdireccion: newS.subdireccion,
                    categoria: newS.categoria,
                    filepaths: newS.filepaths || [],
                    sharepoint: newS.sharepoint,
                    description: newS.description,
                    id_dnp: newS.id_dnp
                };
            });
        });
    };

    const handleDeleteSource = (source: Source) => {
        const remainingSources = sources.filter(s => s.id !== source.id);
        setSources(remainingSources);

        // Si no quedan más publicaciones, cerrar el modal automáticamente
        if (remainingSources.length === 0) {
            onToggle();
        }
    };


    const toggleDescription = (title: string, description?: string, id_dnp?: string) => {
        if (description) {
            setDescriptionModal({
                isOpen: true,
                title: title,
                description: description,
                id_dnp: id_dnp || ""
            });
        }
    };

    useEffect(() => {
        if (isOpen && mode === 'search') {
            setIsSearchModalOpen(true);
        }
    }, [isOpen, mode]);



    const confirmDelete = () => {
        if (isDeletingAll) {
            setSources([]);
            onToggle(); // Cerrar automáticamente al borrar todo
        } else if (sourceToDelete) {
            const remainingSources = sources.filter(s => s.id !== sourceToDelete.id);
            setSources(remainingSources);
            if (remainingSources.length === 0) {
                onToggle();
            }
        }
        setIsConfirmDeleteOpen(false);
        setIsDeletingAll(false);
        setSourceToDelete(null);
    };

    const cancelDelete = () => {
        setSourceToDelete(null);
        setIsDeletingAll(false);
        setIsConfirmDeleteOpen(false);
    };

    const handleDeleteAllSources = () => {
        setIsDeletingAll(true);
        setIsConfirmDeleteOpen(true);
    };

    const selectedCount = sources.filter(s => s.selected).length;
    // Helper to check if a string is a valid SharePoint URL
    const isValidSharepoint = (url?: string) => {
        return !!url && url.startsWith("http") && url.includes("sharepoint.com");
    };

    const handleViewDoc = (sharepointUrl: string) => {
        window.open(sharepointUrl, '_blank', 'noopener,noreferrer');
    };

    if (!isOpen) return null;

    if (mode === 'search') {
        return (
            <SearchModal
                isOpen={isSearchModalOpen}
                initialSelectedIds={sources.filter(s => s.selected).map(s => s.id_dnp || s.id)}
                onClose={() => {
                    setIsSearchModalOpen(false);
                    onToggle();
                }}
                onSave={(newSources) => {
                    handleAddSources(newSources);
                    setIsSearchModalOpen(false);
                    onToggle();
                }}
            />
        );
    }

    return (
        <>
            <div className={styles.modalOverlay} onClick={onToggle}>
                <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.modalHeader}>
                        <div className={styles.modalTitleContainer}>
                            <h2 className={styles.modalTitle}>Publicaciones seleccionadas</h2>
                            <p className={styles.modalSubtitle}>En el chat interactuarás solamente con estas publicaciones</p>
                        </div>
                        <button className={styles.modalCloseButton} onClick={onToggle} aria-label="Cerrar">
                            <Dismiss24Regular />
                        </button>
                    </div>

                    <div className={styles.modalBody}>

                        <div className={styles.actionButtonsRow}>
                            <ActionButton
                                variant="primary"
                                onClick={() => setIsSearchModalOpen(true)}
                                className={styles.actionRowButton}
                            >
                                Seleccionar más publicaciones
                            </ActionButton>

                            {sources.length > 0 && (
                                <ActionButton
                                    variant="danger"
                                    onClick={handleDeleteAllSources}
                                    className={styles.actionRowButton}
                                >
                                    Remover todas
                                </ActionButton>
                            )}
                        </div>

                        <div className={styles.sourcesList}>
                            {sources.map((source) => (
                                <div key={source.id} className={styles.sourceItem}>
                                    <div className={styles.sourceHeader}>
                                        <Document24Regular className={styles.sourceIcon} />
                                        <div className={styles.sourceActions}>
                                            {source.description && (
                                                <button
                                                    className={styles.actionButton}
                                                    onClick={() => toggleDescription(source.title, source.description, source.id_dnp)}
                                                    title="Ver descripción"
                                                    aria-label="Ver descripción"
                                                >
                                                    <Eye24Regular />
                                                </button>
                                            )}
                                            {isValidSharepoint(source.sharepoint) && (
                                                <button
                                                    className={styles.actionButton}
                                                    onClick={() => handleViewDoc(source.sharepoint!)}
                                                    title="Consultar documentos asociados"
                                                    aria-label="Consultar documentos asociados"
                                                >
                                                    <Open24Regular />
                                                </button>
                                            )}
                                            <button
                                                className={`${styles.actionButton} ${styles.deleteAction}`}
                                                onClick={() => handleDeleteSource(source)}
                                                title="Remover publicación"
                                                aria-label="Remover publicación"
                                            >
                                                <Delete24Regular />
                                            </button>
                                        </div>
                                    </div>
                                    <div className={styles.sourceInfo}>
                                        <div className={styles.sourceTitle}>{formatStudyTitle(source.title)}</div>
                                        <div className={styles.sourceDetails}>
                                            {source.pages} documento{source.pages === 1 ? '' : 's'}
                                        </div>
                                        {/* <div className={styles.sourceTimestamp}>
                                            {source.lastAccessed}
                                        </div> */}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className={styles.modalFooter}>
                        <ActionButton variant="primary" onClick={onToggle} className={styles.footerButton}>
                            Continuar al chat
                        </ActionButton>
                    </div>
                </div>
            </div>

            <SearchModal
                isOpen={isSearchModalOpen}
                initialSelectedIds={sources.filter(s => s.selected).map(s => s.id_dnp || s.id)}
                onClose={() => setIsSearchModalOpen(false)}
                onSave={(newSources) => {
                    handleAddSources(newSources);
                    setIsSearchModalOpen(false);
                }}
            />

            <ConfirmDeleteModal
                isOpen={isConfirmDeleteOpen}
                sourceName={isDeletingAll ? "todas las publicaciones" : (sourceToDelete?.title || "")}
                onConfirm={confirmDelete}
                onCancel={cancelDelete}
            />

            <DescriptionModal
                isOpen={descriptionModal.isOpen}
                onClose={() => setDescriptionModal({ isOpen: false, title: "", description: "", id_dnp: "" })}
                title={descriptionModal.title}
                description={descriptionModal.description}
                id_dnp={descriptionModal.id_dnp}
            />
        </>
    );
};


